export const DEVICE_ID_KEY = "_bh_device_id";

// Memória local de segurança em caso de bloqueio de cookies/storage
let memoryFallbackId: string | null = null;

const safeStorageGet = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key);
    }
  } catch (e) {}
  return memoryFallbackId;
};

const safeStorageSet = (key: string, value: string): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {}
  memoryFallbackId = value;
};

/**
 * Retorna o ID antigo baseado no LocalStorage (UUID).
 * Útil para migração de contas já cadastradas.
 */
export const getOldDeviceId = (): string | null => {
  return safeStorageGet(DEVICE_ID_KEY);
};

/**
 * Limpa o ID local.
 */
export const resetLocalDeviceId = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(DEVICE_ID_KEY);
    }
  } catch (e) {}
  memoryFallbackId = null;
};

/**
 * Gera um hash SHA-256 seguro com fallback sem quebrar se crypto.subtle não existir
 */
const sha256 = async (message: string): Promise<string> => {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
      const msgBuffer = new TextEncoder().encode(message);
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {}

  // Hash algorítmico FNV-1a para contextos não-seguros ou WebViews antigas
  let hash = 0x811c9dc5;
  for (let i = 0; i < message.length; i++) {
    hash ^= message.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return ('0000000' + (hash >>> 0).toString(16)).slice(-8) + 'a' + message.length.toString(16);
};

/**
 * Coleta métricas de hardware imutáveis do dispositivo.
 */
const getHardwareFingerprint = (): string => {
  const data: any = {};
  
  // Resolução da tela (independente de orientação)
  if (typeof window !== 'undefined' && window.screen) {
    const width = window.screen.width || 0;
    const height = window.screen.height || 0;
    data.screen = `${Math.max(width, height)}x${Math.min(width, height)}`;
    data.colorDepth = window.screen.colorDepth || 24;
    data.pixelRatio = window.devicePixelRatio || 1;
  }

  // CPU e RAM
  if (typeof navigator !== 'undefined') {
    data.hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
    data.deviceMemory = (navigator as any).deviceMemory || 'unknown';
    data.maxTouchPoints = navigator.maxTouchPoints || 0;
    data.platform = navigator.platform || 'unknown';
  }

  // GPU Renderer (com proteção para evitar crash de WebGL em Mali / Adreno)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        data.gpu = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    // ignorar falhas de canvas
  }

  return JSON.stringify(data);
};

/**
 * Retorna um identificador persistente do dispositivo físico,
 * baseado no hardware (cross-browser). Nunca lança exceção.
 */
export const getDeviceId = async (): Promise<string> => {
  try {
    const fingerprintStr = getHardwareFingerprint();
    const hash = await sha256(fingerprintStr);
    safeStorageSet(DEVICE_ID_KEY, hash);
    return hash;
  } catch (e) {
    console.warn("Aviso ao gerar hardware ID, usando fallback seguro.", e);
    let fallback = safeStorageGet(DEVICE_ID_KEY);
    if (!fallback || fallback.length < 16) {
      fallback = 'fallback-' + Date.now() + '-' + Math.random().toString(36).substring(2);
      safeStorageSet(DEVICE_ID_KEY, fallback);
    }
    return fallback;
  }
};
